import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { ClickAwayListener } from '@mui/base/ClickAwayListener'

import EditorTheme from './EditorTheme.js'
import TreeViewPlugin from './TreeViewPlugin.jsx'
import { EditorState } from 'lexical'
import { useMemo, useRef, useState } from 'react'
import FloatingLinkEditorPlugin from './FloatingLinkEditorPlugin/index.jsx'
import { MATCHERS, validateUrl } from '../utils/url.js'

function Placeholder({ placeholder }: { placeholder: string }) {
  return (
    <div className="editor-placeholder pointer-events-none absolute left-0 top-3 select-none">
      {placeholder}
    </div>
  )
}

export const Editor = ({
  initialValue,
  placeholder,
  autoFocus,
  onChange,
}: {
  initialValue?: string
  autoFocus?: boolean
  placeholder?: string
  onChange?: (val: EditorState) => void
}) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const editorConfig = useMemo((): InitialConfigType => {
    return {
      namespace: 'habitus',
      theme: EditorTheme,
      nodes: [AutoLinkNode, LinkNode],
      editorState: initialValue ? initialValue : undefined,
      onError(error: Error) {
        throw error
      },
    }
  }, [])
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false)
  const [focusElement, setFocusElement] = useState<HTMLElement | null>(null)
  const [isLink, setIsLink] = useState(false)
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null)
  const [showTreeView, setShowTreeView] = useState(() =>
    Boolean(localStorage.getItem('showTreeView')),
  )

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <ClickAwayListener
        onClickAway={() => {
          if (focusElement) {
            setFocusElement(null)
          }
        }}
      >
        <div className="editor-container flex flex-1 flex-col" ref={editorContainerRef}>
          <div
            className="editor-inner prose prose-zinc prose-p:my-3 relative flex flex-1 flex-col"
            ref={onRef}
          >
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input flex-1 outline-none" />}
              // placeholder={placeholder}
              placeholder={placeholder ? <Placeholder placeholder={placeholder} /> : null}
              ErrorBoundary={LexicalErrorBoundary as any}
            />
            <HistoryPlugin />
            {autoFocus && <AutoFocusPlugin />}
            <LinkPlugin validateUrl={validateUrl} />
            <AutoLinkPlugin matchers={MATCHERS} />
            {floatingAnchorElem && (
              <FloatingLinkEditorPlugin
                focusElement={focusElement}
                setFocusElement={setFocusElement}
                isLink={isLink}
                setIsLink={setIsLink}
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
            )}
            {onChange && (
              <OnChangePlugin
                ignoreHistoryMergeTagChange
                ignoreSelectionChange
                onChange={onChange}
              />
            )}
            {false && import.meta.env.DEV && (
              <>
                <button
                  onClick={() => {
                    setShowTreeView((prev) => {
                      const next = !prev
                      if (next) {
                        localStorage.setItem('showTreeView', '1')
                      } else {
                        localStorage.removeItem('showTreeView')
                      }
                      return next
                    })
                  }}
                >
                  ✅ Toggle tree view
                </button>
                {showTreeView && <TreeViewPlugin />}
              </>
            )}
          </div>
          {/* <EditorToolbar /> */}
        </div>
      </ClickAwayListener>
    </LexicalComposer>
  )
}
